/**
 * Capital against the team's own limits: Reported and Marked CET1 each read against their own limit at one basis (the year's worst quarter-end on the draft's projection, else the latest year-end), the room in percentage points,
 * which limit is closest, and the Marked limit translated into Reported terms at the bond losses of that same reading. Pure; the Cockpit tile, the Decide panel and the capital desk's warning all read it.
 */
import type { ContentCatalog, DraftView, TeamState } from "@bsim/contracts";
import type { KpiId } from "@bsim/domain";
import { WORST_QUARTER_END } from "../api/riskFields";
import { formatKpi, formatPoints, formatRate } from "../format";
import { labelOf } from "../format/labels";
import { latestKpis, limitFor } from "./derive";

export type CapitalKpi = "cet1Reported" | "cet1Marked";
export type CapitalBasis = "worstQuarterEnd" | "latestYearEnd";

/** One measure read against its own limit: `own` is false while the limit is the game's usual one (nothing saved yet). */
export interface CapitalReading { readonly kpi: CapitalKpi; readonly reading: number; readonly limit: number; readonly own: boolean }
export interface CapitalRow {
  readonly kpi: CapitalKpi; readonly name: string; readonly value: string; readonly limit: string; readonly own: boolean;
  /** "+0.42 pts" or "−0.15 pts": reading less limit, in percentage points, as printed. */
  readonly room: string; readonly roomPoints: number;
  /** The row with the least room (both on a tie); never set when only one measure has a limit. */
  readonly closest: boolean;
}
export interface CapitalLimits {
  readonly basis: CapitalBasis;
  readonly rows: readonly CapitalRow[];
  /** The Marked limit in Reported terms at the readings' own bond losses, when both measures and the Marked limit are present. */
  readonly translatedReportedRate: number | null;
  readonly translation: string | null;
}

const CAPITAL_KPIS: readonly CapitalKpi[] = ["cet1Reported", "cet1Marked"];
/** The basis words of the heading and the translation: the one phrase the limit test uses, in a heading's form. */
export const BASIS_HEADING: Readonly<Record<CapitalBasis, string>> = { worstQuarterEnd: `${WORST_QUARTER_END} this year`, latestYearEnd: "latest year-end" };
const BASIS_LOSSES: Readonly<Record<CapitalBasis, string>> = { worstQuarterEnd: "this year’s worst-quarter-end bond losses", latestYearEnd: "the latest year-end bond losses" };
/** Printed room: whole hundredths of a point, so two rows that print the same room tie. */
const roomOf = (r: CapitalReading): number => Math.round((r.reading - r.limit) * 10_000) / 100;
/** Idle capital drags returns once both limits are this far (rate) below the reading. */
export const CAPITAL_COMFORT_RATE = 0.03;

export function capitalLimitsOf(readings: readonly CapitalReading[], basis: CapitalBasis): CapitalLimits | null {
  const ordered = CAPITAL_KPIS.flatMap((k) => readings.filter((r) => r.kpi === k));
  if (ordered.length === 0) return null;
  const rooms = ordered.map(roomOf);
  const least = Math.min(...rooms);
  const rows = ordered.map((r, i): CapitalRow => ({
    kpi: r.kpi, name: labelOf(r.kpi), value: formatKpi(r.kpi, r.reading), limit: formatKpi(r.kpi, r.limit), own: r.own,
    room: formatPoints(rooms[i] as number, { signed: true, short: true }), roomPoints: rooms[i] as number, closest: ordered.length > 1 && rooms[i] === least,
  }));
  const reported = ordered.find((r) => r.kpi === "cet1Reported"), marked = ordered.find((r) => r.kpi === "cet1Marked");
  const translated = reported === undefined || marked === undefined ? null : marked.limit + (reported.reading - marked.reading);
  const translation = translated === null || marked === undefined ? null
    : `${marked.own ? "Your" : "The usual"} ${labelOf("cet1Marked")} limit of ${formatKpi("cet1Marked", marked.limit)} is about ${formatRate(translated, 1)} on ${labelOf("cet1Reported")} at ${BASIS_LOSSES[basis]}.`;
  return { basis, rows, translatedReportedRate: translated, translation };
}

/** The readings the draft's projection carries (`RiskProjection.limits`, the quarter-end nearest each limit), when it carries both-or-either; null when it carries neither (a measure masked until the forecast is in). */
export function projectedCapitalReadings(projection: DraftView["riskProjection"] | undefined, own: (kpi: KpiId) => boolean): readonly CapitalReading[] {
  return CAPITAL_KPIS.flatMap((kpi): CapitalReading[] => {
    const l = projection?.limits?.find((x) => x.kpi === kpi);
    return l === undefined ? [] : [{ kpi, reading: l.lowestQuarterEnd, limit: l.limit, own: own(kpi) }];
  });
}

/** What the Cockpit reads: the draft's worst-quarter-end projection when it has one, else the latest released year-end figures against the limits in force. */
export function cockpitCapitalLimits(team: TeamState, catalog: ContentCatalog): CapitalLimits | null {
  const own = (kpi: KpiId): boolean => limitFor(team, kpi, catalog)?.isDefault === false;
  const projected = projectedCapitalReadings(team.draft?.riskProjection, own);
  if (projected.length > 0) return capitalLimitsOf(projected, "worstQuarterEnd");
  const now = latestKpis(team);
  const actual = CAPITAL_KPIS.flatMap((kpi): CapitalReading[] => {
    const l = limitFor(team, kpi, catalog), v = now[kpi];
    return l === null || v === undefined ? [] : [{ kpi, reading: v, limit: l.limit, own: !l.isDefault }];
  });
  return capitalLimitsOf(actual, "latestYearEnd");
}

/** The tile's one line: the closest limit and its room; well clear of every limit, the cost of idle capital. */
export function capitalHint(block: CapitalLimits | null): string | null {
  const lead = block?.rows.find((r) => r.closest) ?? block?.rows[0];
  if (block === null || lead === undefined) return null;
  if (block.rows.every((r) => r.roomPoints > CAPITAL_COMFORT_RATE * 100)) return "Well above your limits \u2014 idle capital drags returns";
  const names = block.rows.filter((r) => r.closest).map((r) => r.name);
  const room = lead.roomPoints < 0 ? `${lead.room.replace(/^\u2212/, "")} past it` : `${lead.room.replace(/^\+/, "")} of room`;
  return `Closest limit: ${names.length === 0 ? lead.name : names.join(" and ")}, ${room} (${BASIS_HEADING[block.basis]})`;
}

/** The capital desk's warning: a buyback target (on Reported CET1) below the Reported level the Marked limit matches at the same bond losses; buybacks stop only at the target, so they could run Marked CET1 through its limit. Null when there is no such target or it is high enough. */
export function buybackBelowMarkedLimit(block: CapitalLimits | null, targetRate: number | null | undefined): string | null {
  const translated = block?.translatedReportedRate;
  const marked = block?.rows.find((r) => r.kpi === "cet1Marked");
  if (block === null || translated === null || translated === undefined || marked === undefined || targetRate === null || targetRate === undefined || targetRate >= translated) return null;
  return `Your buyback target of ${formatKpi("cet1Reported", targetRate)} ${labelOf("cet1Reported")} is below ${formatRate(translated, 1)}, the Reported level that matches ${marked.own ? "your" : "the usual"} ${labelOf("cet1Marked")} limit at ${BASIS_LOSSES[block.basis].replace("this year’s", "this year’s")}, so buybacks alone could take ${labelOf("cet1Marked")} below ${marked.own ? "your" : "that"} limit.`;
}
